import type { PopoverProps } from 'antd';
import { WorkOrderFilterCategory, WorkOrderFilterCondition, WorkOrderFilterJoin } from '../../../enums/workOrderFilter';
import { WorkOrderSort } from '../../../enums/workOrderSort';
import { WorkOrderStatus } from '../../../enums/workOrderStatus';
import { WorkOrderPanel } from '../../../enums/workOrderView';
import { AllowedRoles } from '../../../routes/routeConstants/allowedRoles';
import type { WorkOrderFilterRule } from '../../../types/InfrastructureManagerTypes';

export const VIEW_SEARCH_PARAM = 'view';

export const WORK_ORDER_DRAG_TYPE = 'work-order';
export const WORK_ORDER_DRAG_ROLES = AllowedRoles.EDITOR_AND_ABOVE;

export const COLUMN_MIN_REM = 9.0625;
export const COLUMN_MAX_REM = 23.4375;
export const COLUMN_DEFAULT_REM = 12.5;
export const RESIZE_STEP_REM = 0.5;
// Resize handles report widths as px at the 16px design base (145–375).
export const ARIA_PX_PER_REM = 16;
// All visible columns together may take at most this share of the content width.
export const LIST_MAX_WIDTH_RATIO = 0.65;

export const DEFAULT_SORT = WorkOrderSort.DATE_ASSIGNED;

export const SORT_OPTIONS: { value: WorkOrderSort; label: string }[] = [
  { value: WorkOrderSort.DATE_ASSIGNED, label: 'Date Assigned' },
  { value: WorkOrderSort.NEWEST_EDITED, label: 'Newest (edited)' },
  { value: WorkOrderSort.OLDEST_EDITED, label: 'Oldest (edited)' },
  { value: WorkOrderSort.TITLE_ASC, label: 'A–Z' },
  { value: WorkOrderSort.TITLE_DESC, label: 'Z–A' },
];

export const STATUS_ORDER: WorkOrderStatus[] = [
  WorkOrderStatus.SUBMITTED,
  WorkOrderStatus.SCHEDULED,
  WorkOrderStatus.COMPLETED,
  WorkOrderStatus.ON_HOLD,
  WorkOrderStatus.ARCHIVED,
];

export const STATUS_LABELS: Record<WorkOrderStatus, string> = {
  [WorkOrderStatus.SUBMITTED]: 'Submitted',
  [WorkOrderStatus.SCHEDULED]: 'Scheduled',
  [WorkOrderStatus.COMPLETED]: 'Completed',
  [WorkOrderStatus.ON_HOLD]: 'On Hold',
  [WorkOrderStatus.ARCHIVED]: 'Archived',
};

// Status update list order (Image 17).
export const STATUS_UPDATE_ORDER: WorkOrderStatus[] = [
  WorkOrderStatus.SUBMITTED,
  WorkOrderStatus.SCHEDULED,
  WorkOrderStatus.ON_HOLD,
  WorkOrderStatus.ARCHIVED,
  WorkOrderStatus.COMPLETED,
];

export const WORK_ORDER_DATETIME_FORMAT = 'h:mm A, MMMM D, YYYY';

export const FILTER_CATEGORY_OPTIONS: { value: WorkOrderFilterCategory; label: string }[] = [
  { value: WorkOrderFilterCategory.LAYER, label: 'Layer' },
  { value: WorkOrderFilterCategory.ASSIGNED_TO, label: 'Assigned To' },
  { value: WorkOrderFilterCategory.SUBMITTED_BY, label: 'Submitted By' },
  { value: WorkOrderFilterCategory.DATE_SUBMITTED, label: 'Date Submitted' },
  { value: WorkOrderFilterCategory.DATE_SCHEDULED, label: 'Date Scheduled' },
  { value: WorkOrderFilterCategory.DATE_COMPLETED, label: 'Date Completed' },
  { value: WorkOrderFilterCategory.DATE_EDITED, label: 'Date Edited' },
];

export const FILTER_CONDITION_OPTIONS: { value: WorkOrderFilterCondition; label: string }[] = [
  { value: WorkOrderFilterCondition.IS, label: 'Is' },
  { value: WorkOrderFilterCondition.IS_NOT, label: 'Is Not' },
];

export const FILTER_JOIN_OPTIONS: { value: WorkOrderFilterJoin; label: string }[] = [
  { value: WorkOrderFilterJoin.AND, label: 'And' },
  { value: WorkOrderFilterJoin.OR, label: 'Or' },
];

export const DATE_FILTER_CATEGORIES = new Set<WorkOrderFilterCategory>([
  WorkOrderFilterCategory.DATE_SUBMITTED,
  WorkOrderFilterCategory.DATE_SCHEDULED,
  WorkOrderFilterCategory.DATE_COMPLETED,
  WorkOrderFilterCategory.DATE_EDITED,
]);

export const NO_FILTERS: WorkOrderFilterRule[] = [];

// Top-left corners pinned together so a popover covers its trigger.
export const POPOVER_OVERLAP_ALIGN: PopoverProps['align'] = { points: ['tl', 'tl'] };

export const PANEL_TABS: { panel: WorkOrderPanel; label: string }[] = [
  { panel: WorkOrderPanel.CALENDAR, label: 'Calendar' },
  { panel: WorkOrderPanel.MAP, label: 'Map' },
];

// Sunday first, matching dayjs' default `startOf('week')`.
export const CALENDAR_WEEKDAY_LABELS = ['Sun', 'Mon', 'Tues', 'Wed', 'Thurs', 'Fri', 'Sat'];
export const CALENDAR_DAY_KEY_FORMAT = 'YYYY-MM-DD';
